import { useMemo } from "react";
import GroupsIcon from "@mui/icons-material/Groups";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { formatDelta } from "./insights/insightsFormat";
import { deltaClass, soft } from "./insights/softClasses";
import { aggregateGoals, groupAverage, memberRate } from "../../../lib/progressCompare";
import { ACTIVITY_LABEL_KEYS } from "../../goals/goalRules";

/**
 * Hedef bazında toplam ilerleme; grubun hangi hedefte iyi/zayıf olduğunu gösterir.
 * Hedef kimliği ad + biçim (aynı ad farklı biçim ayrı satır). Liste kırpılmıyor,
 * kutu kendi içinde kaydığı için tüm hedefler görünebiliyor.
 */
export function aggregateLabels(userStats, activityName) {
  return aggregateGoals(userStats, activityName);
}

export function summarize(userStats) {
  const list = Array.isArray(userStats) ? userStats : [];
  const activeMembers = list.filter((u) => (u.daysActiveCount || 0) > 0).length;
  const totalActiveDays = list.reduce((sum, u) => sum + (u.daysActiveCount || 0), 0);
  const avgCompletion = groupAverage(list.map(memberRate));
  return { activeMembers, totalActiveDays, avgCompletion, memberCount: list.length };
}

function StatRow({ Icon, value, label, extra }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-terakkiBorder dark:border-white/15 shrink-0">
      <span className="w-9 h-9 rounded-full bg-terakkiBrandCloud text-terakkiPrimaryDark flex items-center justify-center shrink-0 dark:bg-white/10 dark:text-terakkiBrandSoft">
        <Icon sx={{ fontSize: 18 }} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-xs ${soft.muted}`}>{label}</div>
        <div className="flex items-baseline gap-2">
          <span className={`text-xl font-light ${soft.accent}`}>{value}</span>
          {extra}
        </div>
      </div>
    </div>
  );
}

/**
 * Sol sütun: özet satırları ve hedef kırılımı, alt alta; kutusuz, çizgiyle
 * ayrılmış (mobildeki yumuşak düzen). averageDelta: ortalamanın bir önceki
 * eşit döneme göre farkı; compareText: "geçen haftaya göre" gibi.
 */
export default function ProgressSummary({ userStats, averageDelta = null, compareText }) {
  const { t } = useScriptMode();
  const summary = useMemo(() => summarize(userStats), [userStats]);
  const labels = useMemo(
    () => aggregateLabels(userStats, (activity) => t(ACTIVITY_LABEL_KEYS[activity] || activity)),
    [userStats, t]
  );

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0">
      <StatRow
        Icon={TrendingUpIcon}
        value={`%${summary.avgCompletion}`}
        label={t("manage_stats_summary_avg_completion")}
        extra={
          averageDelta !== null ? (
            <span className={`text-xs font-medium ${deltaClass(averageDelta)}`} title={compareText}>
              {formatDelta(averageDelta)} {compareText ? <span className={soft.faint}>{compareText}</span> : null}
            </span>
          ) : null
        }
      />
      <StatRow
        Icon={GroupsIcon}
        value={`${summary.activeMembers}/${summary.memberCount}`}
        label={t("manage_stats_summary_active_members")}
      />
      <StatRow
        Icon={EventAvailableIcon}
        value={summary.totalActiveDays}
        label={t("manage_stats_summary_active_days")}
      />

      {labels.length > 0 && (
        <div className="pt-4 flex flex-col lg:flex-1 lg:min-h-0">
          <h4 className={`text-xs font-medium tracking-wide mb-3 shrink-0 ${soft.muted}`}>
            {t("manage_stats_chart_title")}
          </h4>
          <ul className="flex flex-col gap-3 max-h-64 overflow-y-auto lg:max-h-none lg:flex-1 lg:min-h-0 pr-1">
            {labels.map((item) => (
              <li key={item.key} data-label={item.label}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`text-xs truncate ${soft.ink}`}>{item.name}</span>
                  <span className={`text-xs font-medium shrink-0 ${soft.accent}`}>
                    %{item.completion}
                  </span>
                </div>
                <div className={soft.track}>
                  <div
                    className={soft.fill}
                    style={{ width: `${Math.min(100, item.completion)}%` }}
                  />
                </div>
                <div className={`text-[11px] mt-0.5 ${soft.faint}`}>
                  {item.value} / {item.goalValue}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
